import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type ToastKind = 'success' | 'error' | 'info';

type Toast = {
  id: string;
  kind: ToastKind;
  title?: string;
  message: string;
};

type ToastApi = {
  push: (t: Omit<Toast, 'id'>) => void;
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
};

const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const remove = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (t: Omit<Toast, 'id'>) => {
      const id = crypto.randomUUID();
      const toast: Toast = { id, ...t };
      setToasts((prev) => [...prev, toast]);
      setTimeout(() => remove(id), t.kind === 'error' ? 6500 : 3500);
    },
    [remove]
  );

  const api: ToastApi = useMemo(
    () => ({
      push,
      success: (message: string, title?: string) => push({ kind: 'success', title, message }),
      error: (message: string, title?: string) => push({ kind: 'error', title, message }),
      info: (message: string, title?: string) => push({ kind: 'info', title, message }),
    }),
    [push]
  );

  return (
    <ToastCtx.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onClose={remove} />
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

function ToastViewport({ toasts, onClose }: { toasts: Toast[]; onClose: (id: string) => void }) {
  if (!toasts.length) return null;
  return (
    <div className="toast-viewport" role="region" aria-label="Notifications">
      {toasts.map((t) => (
        <div key={t.id} className={`toast toast--${t.kind}`} role="status">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'baseline' }}>
            <div style={{ fontWeight: 900 }}>{t.title || (t.kind === 'success' ? 'Saved' : t.kind === 'error' ? 'Error' : 'Info')}</div>
            <button className="toast__close" onClick={() => onClose(t.id)} aria-label="Close">
              ×
            </button>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            {t.message}
          </div>
        </div>
      ))}
    </div>
  );
}

